import { ICompanyProfileResponse } from '../../../schemas/CompanyProfile.schema';
import {
  ISourcingFundingRound,
  ISourcingPerson,
  ISourcingProfileData,
  ISourcingTrafficStat,
} from './sourcingProfileMockData';

// Aliases for the inline entry shapes declared on `ISourcingProfileData`.
// Kept local because the upstream types are anonymous tuple elements.
type IFundingEvolutionEntry = ISourcingProfileData['fundingEvolution'][number];
type IWebTrafficEvolutionEntry = NonNullable<ISourcingProfileData['webTrafficEvolution']>[number];

/**
 * Pure adapter that maps the unified profile response returned by the sourcing
 * service into the UI view-model consumed by `SourcingProfilePage`.
 *
 * Design notes:
 *  - The response shape (see `profile-builder.ts` in the sourcing API repo)
 *    carries many nested / optional fields whose presence depends on which
 *    upstream providers matched (Crunchbase / Harmonic / PitchBook). The
 *    adapter is intentionally defensive: every lookup uses safe coercions and
 *    falls back to `null` / `[]` so downstream panels can render gracefully.
 *  - Fields that have no counterpart in the API (foresightSummary narrative,
 *    revenuePerEmployee, enrichedDate, listsCount, per-person
 *    `recentWorkHistory`, etc.) are returned as empty arrays or null. The
 *    existing panels already tolerate that.
 *  - `fundingEvolution` (Growth Metrics chart) is *approximated* from the
 *    current `employee_count` plus `employee_growth.ago_*d` buckets — the API
 *    does not surface the true historical time-series yet. See
 *    `deriveFundingEvolution` below for the formula and caveats.
 */
export function mapCompanyProfileResponse(
  response: ICompanyProfileResponse,
  opts: { readonly fallbackId?: string } = {}
): ISourcingProfileData {
  const { profile } = response;
  const identity = asObject(profile.identity);
  const location = asObject(profile.location);
  const status = asObject(profile.status);
  const classification = asObject(profile.classification);
  const headcount = asObject(profile.headcount);
  const funding = asObject(profile.funding);
  const socialPresence = asObject(profile.social_presence);
  const contact = asObject(profile.contact);
  const traction = asObject(profile.traction);
  const corporateStructure = asObject(profile.corporate_structure);

  const sourceIds = asObject(identity.source_ids);
  const id = pickId(identity, sourceIds, opts.fallbackId);
  const website = asString(identity.homepage_url) ?? asString(identity.domain);

  const sector = deriveSector(status, classification);
  const industry = deriveListFromStringOrArray(classification.industry);
  const keywords = deriveListFromStringOrArray(classification.keywords);
  const tags = buildTags(status, classification);
  const universe = status.universe ? [asString(status.universe) ?? ''].filter(Boolean) : [];

  const fundingRounds = mapFundingRounds(funding);
  const fundingEvolution = deriveFundingEvolution(headcount);
  const webTrafficEvolution = deriveWebTrafficEvolution(traction);
  const people = Array.isArray(profile.people) ? profile.people : [];
  const { ceoName, founderPeople, keyPeople } = mapPeople(people);
  const founderNames = founderPeople.map((p) => p.name);
  const highlights = mapHighlights(classification.highlights);
  const trafficStats = buildTrafficStats(socialPresence, traction, headcount);

  const linkedinUrl = deepString(socialPresence, 'linkedin', 'url');
  const twitterUrl = deepString(socialPresence, 'twitter', 'url');
  const facebookUrl = deepString(socialPresence, 'facebook', 'url');
  const crunchbaseUrl = asString(identity.cb_url);
  const pitchbookUrl = asString(identity.pb_url);
  // Harmonic has no public permalink we can derive from the payload; the
  // header currently only toggles the vendor badge based on URL presence so
  // this is intentionally null until the API exposes one.
  const harmonicUrl = null;

  const email = asString(contact.email) ?? firstStringInList(contact.all_emails);
  const phone = asString(contact.phone) ?? firstStringInList(contact.all_phone_numbers);

  const totalRaised = toNumber(funding.total_funding_usd);
  const foundedYear = parseFoundedYear(identity.year_founded);
  const hq = buildHq(location);
  const secondaryHq = null;
  const acquiredByRaw = asObject(corporateStructure.acquired_by);
  const acquiredBy = asString(acquiredByRaw.name);

  return {
    id,
    name: asString(identity.name) ?? id,
    logoUrl: asString(identity.logo_url),
    website: website,
    description: asString(identity.short_description) ?? asString(identity.long_description) ?? '',
    tags,
    enrichedDate: '',
    listsCount: undefined,

    trafficStats,
    operationalStatus: asString(status.operating_status),

    ceoName,
    founders: founderNames,
    foundedYear,
    headcount: deriveHeadcountLabel(headcount),
    totalRaised,
    annualRevenue: null,
    revenuePerEmployee: null,
    hq,
    sector,
    industry,
    keywords,
    universe,

    linkedinUrl,
    twitterUrl,
    facebookUrl,
    crunchbaseUrl,
    pitchbookUrl,
    harmonicUrl,

    foresightSummary: [],
    fundingEvolution,
    webTrafficEvolution,
    fundingRounds,
    highlights,

    legalName: asString(identity.legal_name),
    companyType: asString(status.company_type),
    alsoKnownAs: toStringArray(identity.also_known_as),
    secondaryHq,
    acquiredBy,

    keyPeople,
    founderPeople,

    email,
    phone,

    similarCompanies: [],
  };
}

// ─── helpers: coercion ─────────────────────────────────────────────────────

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toNumber(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((v) => asString(v)).filter((v): v is string => v != null);
}

function firstStringInList(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  for (const entry of value) {
    const s = asString(entry);
    if (s) {
      return s;
    }
  }
  return null;
}

function deepString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  let current: unknown = obj;
  for (const key of keys) {
    current = asObject(current)[key];
  }
  return asString(current);
}

// ─── helpers: domain mappings ──────────────────────────────────────────────

/**
 * Prefer stable identifiers in this order so `id` can round-trip through the
 * UI (e.g. as React keys): CB UUID → HM URN → PB id → HM id → domain → the
 * caller-provided fallback (i.e. the raw `:companyId` param).
 */
function pickId(
  identity: Record<string, unknown>,
  sourceIds: Record<string, unknown>,
  fallback: string | undefined
): string {
  return (
    asString(sourceIds.cb_uuid) ??
    asString(sourceIds.hm_entity_urn) ??
    asString(sourceIds.pb_company_id) ??
    asString(sourceIds.hm_id) ??
    asString(identity.domain) ??
    fallback ??
    ''
  );
}

/**
 * `year_founded` can be a year int ("2014") or an ISO date string
 * ("2020-01-01T00:00:00+00:00"). Returns a 4-digit year or null.
 */
function parseFoundedYear(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1000 && value < 3000 ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}$/.test(trimmed)) {
      return Number.parseInt(trimmed, 10);
    }
    const parsed = new Date(trimmed);
    const year = parsed.getUTCFullYear();
    return Number.isFinite(year) ? year : null;
  }
  return null;
}

function buildHq(location: Record<string, unknown>): string | null {
  const formatted = asString(location.address_formatted);
  if (formatted) {
    return formatted;
  }
  const parts = [asString(location.city), asString(location.state), asString(location.country)].filter(
    (p): p is string => p != null
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

function deriveSector(status: Record<string, unknown>, classification: Record<string, unknown>): string[] {
  const universe = asString(status.universe);
  if (universe) {
    return [universe];
  }
  // Fall back to sub_industry / product_type tags when no PB universe is present.
  const sub = toStringArray(classification.sub_industry);
  if (sub.length > 0) {
    return sub;
  }
  return [];
}

function deriveListFromStringOrArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return toStringArray(value);
  }
  const s = asString(value);
  if (!s) {
    return [];
  }
  // Heuristic: CB `CATEGORY_LIST` is a comma-separated string. The API may
  // surface it as a raw string or already as an array depending on source.
  if (s.includes(',')) {
    return s
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [s];
}

function buildTags(status: Record<string, unknown>, classification: Record<string, unknown>): string[] {
  const tags: string[] = [];
  const push = (value: string | null) => {
    if (value && !tags.includes(value)) {
      tags.push(value);
    }
  };

  push(asString(status.operating_status));
  push(asString(status.ownership_status));
  push(asString(status.company_type));
  push(asString(status.financing_status));
  push(asString(classification.customer_type));

  return tags;
}

function mapHighlights(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry;
        }
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>;
          return asString(record.text) ?? asString(record.display_value) ?? asString(record.name);
        }
        return null;
      })
      .filter((s): s is string => s != null && s.length > 0);
  }
  const single = asString(value);
  return single ? [single] : [];
}

function deriveHeadcountLabel(headcount: Record<string, unknown>): string | null {
  const range = asString(headcount.employee_count_range);
  if (range) {
    return range;
  }
  const count = toNumber(headcount.employee_count);
  if (count != null) {
    return String(count);
  }
  return null;
}

// ─── helpers: growth buckets (Harmonic `ago_*d` shape) ─────────────────────

/**
 * Trailing windows surfaced by `profile.headcount.employee_growth` and the
 * social `follower_count_growth` blocks — mirrors Harmonic's
 * `ago_14d / ago_30d / ago_90d / ago_180d / ago_365d` buckets.
 *
 * Array order drives the x-axis order of the Growth Metrics chart
 * (`deriveGrowthSeries` iterates in this order). We render
 * **longest → shortest** lookback (365d first, 14d last) so the chart
 * reads chronologically left → right: older snapshots on the left,
 * most-recent window on the right.
 *
 * The unified API has shipped both key forms depending on version:
 *  - flat: `{ '14d': {...}, '30d': {...}, ... }`
 *  - Harmonic-native: `{ 'ago_14d': {...}, 'ago_30d': {...}, ... }`
 *
 * All readers below try both for each window so the chart and KPI cards
 * stay robust across backend changes (see `readBucketForWindow`).
 */
const HEADCOUNT_GROWTH_WINDOWS = ['365d', '180d', '90d', '30d', '14d'] as const;

type HeadcountGrowthWindow = (typeof HEADCOUNT_GROWTH_WINDOWS)[number];

/**
 * Look up a growth bucket under either the flat key (`"14d"`) or the
 * Harmonic-native alias (`"ago_14d"`). The flat form wins when both are
 * present — it's the normalised shape the sourcing API has been migrating
 * towards.
 */
function readBucketForWindow(growthRecord: Record<string, unknown>, window: HeadcountGrowthWindow): unknown {
  const flat = growthRecord[window];
  if (flat != null) {
    return flat;
  }
  return growthRecord[`ago_${window}`];
}

/**
 * Reads a Harmonic `{ change, percent_change, value }` bucket. All three
 * fields may be `null`; we surface each coerced independently so the caller
 * can decide which one to consume.
 *
 * `value` is Harmonic's *past* snapshot (the count at the start of the
 * trailing window, not today) and `percent_change` is the trailing growth
 * as a percentage (e.g. `3.36` means +3.36%).
 */
function readGrowthBucket(bucket: unknown): {
  readonly past: number | null;
  readonly percentChange: number | null;
  readonly change: number | null;
} {
  const record = asObject(bucket);
  return {
    past: toNumber(record.value),
    percentChange: toNumber(record.percent_change),
    change: toNumber(record.change),
  };
}

// ─── helpers: growth evolution (shared Growth Metrics derivation) ──────────

/**
 * Canonical per-window entry shared by the Headcount and Web Traffic tabs.
 * Mapped to `{ headcount }` / `{ visits }` by the thin wrappers below so the
 * view-model carries semantically named fields.
 */
interface IGrowthSeriesEntry {
  readonly stage: HeadcountGrowthWindow;
  readonly past: number;
  readonly growthPct: number;
}

/**
 * Pure derivation of a `{ past, percent_change }` time-series from a generic
 * Harmonic-shaped `growth` object. Shared between `deriveFundingEvolution`
 * (headcount) and `deriveWebTrafficEvolution` (monthly visits) so both tabs
 * of the Growth Metrics chart use the exact same branching / priority logic.
 *
 * Behaviour:
 *  - Windows with neither `value` nor `percent_change` are skipped (keeps
 *    the series sparse rather than emitting noisy zero-bars).
 *  - When `value` is missing but `percent_change` exists, we reconstruct
 *    the past snapshot from the caller's current value (`current / (1 + pct/100)`)
 *    so the chart still renders. Callers pass `null` for `current` when
 *    they have no fresh value to reconstruct from.
 *  - Returns `[]` when the `growth` object is absent / empty, letting the
 *    chart show its empty state.
 */
function deriveGrowthSeries(growth: unknown, current: number | null): IGrowthSeriesEntry[] {
  if (!growth || typeof growth !== 'object') {
    return [];
  }
  const growthRecord = growth as Record<string, unknown>;
  const entries: IGrowthSeriesEntry[] = [];

  for (const window of HEADCOUNT_GROWTH_WINDOWS) {
    const { past, percentChange } = readGrowthBucket(readBucketForWindow(growthRecord, window));
    if (past == null && percentChange == null) {
      continue;
    }

    const resolvedPast =
      past ??
      (current != null && percentChange != null && 1 + percentChange / 100 > 0
        ? Math.max(0, Math.round(current / (1 + percentChange / 100)))
        : null);

    if (resolvedPast == null) {
      continue;
    }

    entries.push({
      stage: window,
      past: resolvedPast,
      growthPct: percentChange ?? 0,
    });
  }

  return entries;
}

// ─── helpers: funding evolution (Growth Metrics — Headcount tab) ───────────

/**
 * Builds the Headcount series from `profile.headcount`. Thin wrapper around
 * `deriveGrowthSeries` that renames `past` → `headcount` to match the
 * view-model's semantic field name.
 */
export function deriveFundingEvolution(headcount: Record<string, unknown>): IFundingEvolutionEntry[] {
  const current = toNumber(headcount.employee_count);
  return deriveGrowthSeries(headcount.employee_growth, current).map((entry) => ({
    stage: entry.stage,
    headcount: entry.past,
    growthPct: entry.growthPct,
  }));
}

// ─── helpers: web traffic evolution (Growth Metrics — Web Traffic tab) ─────

/**
 * Builds the Web Traffic series from `profile.traction.web_traffic.growth`.
 *
 * The payload shape mirrors `employee_growth` exactly — each trailing
 * window (`14d / 30d / ...`) carries `{ change, percent_change, value }`
 * where `value` is the monthly unique visitors at the *start* of the
 * window. We reuse `deriveGrowthSeries` and rename `past` → `visits`.
 *
 * `traction.web_traffic.count` (the current-month value) is used only to
 * reconstruct missing `value` entries when a bucket has `percent_change`
 * but no snapshot — identical fallback as the headcount path.
 */
export function deriveWebTrafficEvolution(traction: Record<string, unknown>): IWebTrafficEvolutionEntry[] {
  const webTraffic = asObject(traction.web_traffic);
  const current = toNumber(webTraffic.count);
  return deriveGrowthSeries(webTraffic.growth, current).map((entry) => ({
    stage: entry.stage,
    visits: entry.past,
    growthPct: entry.growthPct,
  }));
}

// ─── helpers: funding rounds ───────────────────────────────────────────────

/**
 * The backend emits a flat list that mixes Crunchbase and PitchBook
 * rounds — `profile.funding.rounds` simply concatenates the two
 * provider lists, each entry tagged with `source: 'CB' | 'PB'`. The
 * UI wants a single row per real-world round, so we:
 *
 *   1. Map each raw entry into the UI shape (preserving its `source`).
 *   2. Dedup by `(announced_date, round_name)` (lowercased + trimmed).
 *      When a CB and PB entry collide we keep the CB one — Crunchbase
 *      historically has cleaner round-name normalization. Same-source
 *      collisions keep the first-seen entry (preserves backend order).
 *   3. Sort newest-first by `announced_date`.
 *
 * Rounds whose dedup key cannot be built (missing date AND missing
 * stage) bypass dedup entirely so partial data still renders.
 */
export function mapFundingRounds(funding: Record<string, unknown>): ISourcingFundingRound[] {
  const raw = Array.isArray(funding.rounds) ? (funding.rounds as unknown[]) : [];
  const mapped: ISourcingFundingRound[] = [];

  for (const entry of raw) {
    const r = asObject(entry);
    const announced = asString(r.announced_date) ?? asString(r.close_date);
    const stage = deriveRoundStage(r);
    const investors = toStringArray(r.investors);
    const lead = extractLeadInvestors(r, investors);
    const source = parseFundingSource(r.source);

    mapped.push({
      stage,
      round: stage,
      date: announced ?? '',
      raised: toNumber(r.amount_usd) ?? 0,
      postMoney: toNumber(r.post_money_valuation),
      leadInvestors: lead,
      allInvestors: investors,
      ...(source ? { source } : {}),
    });
  }

  return dedupFundingRoundsCbOverPb(mapped).sort((a, b) => compareDatesDesc(a.date, b.date));
}

/**
 * Parse the provider tag emitted by `profile-builder.ts`. Returns
 * `undefined` for unknown / missing values so we never persist a bogus
 * `source` on the UI shape.
 */
function parseFundingSource(value: unknown): 'CB' | 'PB' | undefined {
  if (value === 'CB' || value === 'PB') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const upper = value.trim().toUpperCase();
  if (upper === 'CB' || upper === 'CRUNCHBASE') {
    return 'CB';
  }
  if (upper === 'PB' || upper === 'PITCHBOOK') {
    return 'PB';
  }
  return undefined;
}

/**
 * Dedup overlapping rounds across providers, preferring Crunchbase.
 * Iterates in input order so same-source collisions deterministically
 * keep the first occurrence.
 *
 * Exported for tests.
 */
export function dedupFundingRoundsCbOverPb(
  rounds: readonly ISourcingFundingRound[]
): ISourcingFundingRound[] {
  // Map<key, index in result array>. Indexed lookup lets us swap the
  // winning entry in-place when CB displaces PB without rebuilding the array.
  const indexByKey = new Map<string, number>();
  const result: ISourcingFundingRound[] = [];

  for (const round of rounds) {
    const key = buildRoundDedupKey(round);
    if (key === null) {
      // Can't build a stable key — surface as-is.
      result.push(round);
      continue;
    }

    const existingIdx = indexByKey.get(key);
    if (existingIdx === undefined) {
      indexByKey.set(key, result.length);
      result.push(round);
      continue;
    }

    const existing = result[existingIdx];
    if (existing.source !== 'CB' && round.source === 'CB') {
      // CB beats PB (or unknown) — replace in place to preserve the
      // overall input order for surrounding rounds.
      result[existingIdx] = round;
    }
    // Otherwise: keep `existing` (CB stays winning, or both same source).
  }

  return result;
}

function buildRoundDedupKey(round: ISourcingFundingRound): string | null {
  const date = round.date.trim();
  const stage = round.stage.trim().toLowerCase();
  if (!date && !stage) {
    return null;
  }
  return `${date}|${stage}`;
}

/**
 * Extract the lead investor(s) for a round, preferring explicit provider
 * fields when present and only falling back to "first of `investors`" when
 * no dedicated field is available.
 *
 * Both Crunchbase (`lead_investors`) and PitchBook (`lead_investor` /
 * `lead_investor_name`) expose this separately from the full investor list,
 * and the order inside `investors` is not guaranteed to put the lead first.
 * We also accept arrays of `{ name }` / `{ display_name }` objects since
 * some providers nest the names.
 *
 * When the dedicated field exists but is empty (explicit "no lead"), we
 * return `[]` rather than silently defaulting to `investors[0]` — that was
 * the bug this helper replaces.
 */
function extractLeadInvestors(round: Record<string, unknown>, allInvestors: readonly string[]): string[] {
  const dedicated =
    pickLeadInvestorField(round.lead_investors) ??
    pickLeadInvestorField(round.lead_investor) ??
    pickLeadInvestorField(round.lead_investor_name) ??
    pickLeadInvestorField(round.lead_investor_names);

  if (dedicated != null) {
    return dedicated;
  }

  // No dedicated field on this round — fall back to the first entry of the
  // all-investors list so the column is never blank when data is available.
  return allInvestors.length > 0 ? [allInvestors[0]] : [];
}

/**
 * Normalise a lead-investor field that may be:
 *  - a single string ("Sequoia Capital")
 *  - an array of strings (`["Sequoia Capital", "Accel"]`)
 *  - an array of `{ name }` / `{ display_name }` objects
 *  - `null` / missing (→ returns `null`, signalling "field not present")
 *  - an empty array (→ returns `[]`, signalling "explicitly no lead")
 */
function pickLeadInvestorField(value: unknown): string[] | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const s = asString(value);
    return s ? [s] : [];
  }

  if (Array.isArray(value)) {
    const names = value
      .map((entry) => {
        if (typeof entry === 'string') {
          return asString(entry);
        }
        const record = asObject(entry);
        return asString(record.name) ?? asString(record.display_name);
      })
      .filter((s): s is string => s != null);
    return names;
  }

  return null;
}

function deriveRoundStage(r: Record<string, unknown>): string {
  return (
    asString(r.round_name) ??
    asString(r.round_type) ??
    asString(r.deal_type_2) ??
    asString(r.deal_type) ??
    'Unknown'
  );
}

function compareDatesDesc(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) && Number.isNaN(tb)) {
    return 0;
  }
  if (Number.isNaN(ta)) {
    return 1;
  }
  if (Number.isNaN(tb)) {
    return -1;
  }
  return tb - ta;
}

// ─── helpers: people ───────────────────────────────────────────────────────

/**
 * Partitions the unified `profile.people[]` list into CEO / founders / key
 * people. We keep the heuristic deliberately simple — titles are free-form
 * text and upstream sources inconsistently flag role types.
 */
function mapPeople(raw: unknown[]): {
  readonly ceoName: string | null;
  readonly founderPeople: ISourcingPerson[];
  readonly keyPeople: ISourcingPerson[];
} {
  const founders: ISourcingPerson[] = [];
  const keyPeople: ISourcingPerson[] = [];
  let ceoName: string | null = null;

  for (const entry of raw) {
    const record = asObject(entry);
    const person = toSourcingPerson(record);
    if (!person) {
      continue;
    }

    const title = (person.title ?? '').toLowerCase();
    const isFounder = title.includes('founder') || title.includes('co-founder');
    const isCeo = /\bceo\b|chief executive/i.test(person.title ?? '');

    if (isCeo && ceoName == null) {
      ceoName = person.name;
    }
    if (isFounder) {
      founders.push(person);
    } else {
      keyPeople.push(person);
    }
  }

  return { ceoName, founderPeople: founders, keyPeople };
}

function toSourcingPerson(record: Record<string, unknown>): ISourcingPerson | null {
  const name = asString(record.full_name);
  if (!name) {
    return null;
  }
  const city = asString(record.city);
  const state = asString(record.state);
  const country = asString(record.country);
  const location = [city, state, country].filter((p): p is string => p != null).join(', ');

  return {
    id: asString(record.linkedin_url) ?? name,
    name,
    title: asString(record.title) ?? '',
    tenure: null,
    headline: asString(record.description) ?? null,
    education: null,
    location,
    avatarUrl: asString(record.profile_picture_url),
    linkedinUrl: asString(record.linkedin_url),
    twitterUrl: asString(record.twitter_url),
    facebookUrl: null,
    recentWorkHistory: [],
  };
}

// ─── helpers: traffic stats ────────────────────────────────────────────────

/**
 * Build the four cards rendered in `TrafficStatsRow`. Missing metrics are
 * skipped entirely rather than shown as empty — the panel tolerates a
 * short-or-empty list.
 */
function buildTrafficStats(
  socialPresence: Record<string, unknown>,
  traction: Record<string, unknown>,
  headcount: Record<string, unknown>
): ISourcingTrafficStat[] {
  const stats: ISourcingTrafficStat[] = [];

  const webTraffic = extractLatestCount(asObject(traction.web_traffic));
  const webGrowth = extractGrowthPct(asObject(asObject(traction.web_traffic).growth));
  if (webTraffic != null) {
    stats.push({
      label: 'Monthly Web Traffic',
      value: formatCompact(webTraffic),
      delta: webGrowth != null ? formatDeltaPct(webGrowth) : '',
      deltaPositive: (webGrowth ?? 0) >= 0,
      deltaDescription: webGrowth != null ? 'MoM' : '',
    });
  }

  const linkedin = asObject(socialPresence.linkedin);
  const linkedinCount = extractFollowerCount(linkedin.follower_count);
  const linkedinGrowth = extractGrowthPct(asObject(linkedin.follower_count_growth));
  if (linkedinCount != null) {
    stats.push({
      label: 'LinkedIn Followers',
      value: formatCompact(linkedinCount),
      delta: linkedinGrowth != null ? formatDeltaPct(linkedinGrowth) : '',
      deltaPositive: (linkedinGrowth ?? 0) >= 0,
      deltaDescription: linkedinGrowth != null ? 'over the last 6M' : '',
    });
  }

  const twitter = asObject(socialPresence.twitter);
  const twitterCount = extractFollowerCount(twitter.follower_count);
  const twitterGrowth = extractGrowthPct(asObject(twitter.follower_count_growth));
  if (twitterCount != null) {
    stats.push({
      label: 'X Followers',
      value: formatCompact(twitterCount),
      delta: twitterGrowth != null ? formatDeltaPct(twitterGrowth) : '',
      deltaPositive: (twitterGrowth ?? 0) >= 0,
      deltaDescription: twitterGrowth != null ? 'over the last 6M' : '',
    });
  }

  const headcountLabel = deriveHeadcountLabel(headcount);
  const headcountGrowth = extractGrowthPct(asObject(headcount.employee_growth));
  if (headcountLabel != null) {
    stats.push({
      label: 'Headcount',
      value: headcountLabel,
      delta: headcountGrowth != null ? formatDeltaPct(headcountGrowth) : '',
      deltaPositive: (headcountGrowth ?? 0) >= 0,
      deltaDescription: headcountGrowth != null ? 'over the last 6M' : '',
    });
  }

  return stats;
}

/**
 * Harmonic traction blocks expose a `metrics: [{ timestamp, value }]` array
 * and "ago_14d / ago_30d / ago_90d / ago_180d / ago_365d" growth fields.
 * Here we pluck the most recent point value.
 */
function extractLatestCount(metric: Record<string, unknown>): number | null {
  const direct = toNumber(metric.count);
  if (direct != null) {
    return direct;
  }
  const series = metric.metrics;
  if (Array.isArray(series) && series.length > 0) {
    const last = asObject(series[series.length - 1]);
    return toNumber(last.value);
  }
  return null;
}

/**
 * Social blocks are shape-inconsistent between providers:
 *  - `linkedin.follower_count` comes as a plain number (`9888`).
 *  - `twitter.follower_count` comes as a time-series array of
 *    `{ metric_value, timestamp }` (sorted newest-first in practice but we
 *    don't rely on that — we pick the entry with the latest timestamp,
 *    falling back to the first element).
 * This helper normalises both into a single latest count.
 */
export function extractFollowerCount(value: unknown): number | null {
  const direct = toNumber(value);
  if (direct != null) {
    return direct;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  let latest: { value: number; ts: number } | null = null;
  for (const raw of value) {
    const entry = asObject(raw);
    const metricValue = toNumber(entry.metric_value) ?? toNumber(entry.value);
    if (metricValue == null) {
      continue;
    }
    const ts = Date.parse(asString(entry.timestamp) ?? '');
    const timestamp = Number.isFinite(ts) ? ts : Number.NEGATIVE_INFINITY;
    if (latest == null || timestamp > latest.ts) {
      latest = { value: metricValue, ts: timestamp };
    }
  }

  if (latest != null) {
    return latest.value;
  }

  // No usable timestamps — fall back to the first numeric entry so we at
  // least surface *some* count rather than dropping the card entirely.
  const first = asObject(value[0]);
  return toNumber(first.metric_value) ?? toNumber(first.value);
}

/**
 * Picks the 180d growth point (the UI's default "last 6M" copy). Falls back
 * to 90d then 30d if the longer horizon is missing.
 *
 * Each priority tier tries both key forms (`180d` and `ago_180d`) before
 * advancing to the next tier, so a record that only ships the Harmonic-
 * native `ago_*d` shape doesn't silently fall through to a lower-priority
 * flat key.
 *
 * Each bucket is a `{ change, percent_change, value }` object from Harmonic
 * (mirrored by `buildHeadcount` / `buildSocialPresence` in the API). We
 * read `percent_change` via `readGrowthBucket`, but also accept a raw
 * number for forward-compatibility if the shape ever flattens.
 */
export function extractGrowthPct(growth: Record<string, unknown>): number | null {
  // Priority: 180d ("last 6M") → 90d → 30d. Each tier also tries its
  // `ago_*` alias, matching `deriveFundingEvolution`.
  const tiers: readonly HeadcountGrowthWindow[] = ['180d', '90d', '30d'];
  for (const window of tiers) {
    const raw = readBucketForWindow(growth, window);
    if (raw == null) {
      continue;
    }
    const direct = toNumber(raw);
    if (direct != null) {
      return direct;
    }
    const { percentChange } = readGrowthBucket(raw);
    if (percentChange != null) {
      return percentChange;
    }
  }
  return null;
}

function formatCompact(value: number): string {
  // Intl compact notation avoids a direct FMT dependency — traffic cards want
  // values like "120.5K" rather than raw numbers.
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatDeltaPct(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}
